import { Form, FormValidationType, RunTime, KEY_CODES, Rule, CustomRule, Field } from './type';
import { useState, useEffect } from 'react';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isBoolean from 'lodash/isBoolean';
import includes from 'lodash/includes';
import isString from 'lodash/isString';

export function ensure<T>(argument: T | undefined | null, message = 'This value was promised to be there.'): T {
    if (isNil(argument)) {
        throw new TypeError(message);
    }

    return argument;
}

export function getRule(field: Field, type: FormValidationType): Rule {
    return ensure<Rule>(field.rules?.find(rule => rule.type === type));
}

export const CheckValidationType = {
    [FormValidationType.Required]: (field: Field) => {
        return !(!field.value || (isBoolean(field.value) && !field.value));
    },
    [FormValidationType.NumberOnly]: (field: Field, e?: React.KeyboardEvent<HTMLInputElement>) => {
        if (e) {
            const keyCode = e.keyCode || e.which;
            if (keyCode < KEY_CODES.digit0 || keyCode > KEY_CODES.digit9) {
                e.preventDefault();
            }
            return true;
        } else {
            field.value = (field.value as string).replace(/[\D]+/g, '');
            return true;
        }
    },
    [FormValidationType.MaxLength]: (field: Field, e?: React.KeyboardEvent<HTMLInputElement>) => {
        const value = field.value;
        const rule = getRule(field, FormValidationType.MaxLength);
        if (isNil(rule.value) || isNil(value) || isBoolean(value)) {
            return true;
        }

        if (!isNil(e) && value.length > +rule.value - 1) {
            e.preventDefault();
            return true;
        } else {
            return !(value.length > rule.value && isNil(e));
        }
    },
};

export const testCustomRule = (field: Field, rule: CustomRule, e?: React.KeyboardEvent<HTMLInputElement>): boolean => {
    if (e && includes(rule.runTime, RunTime.OnKeyDown) && !rule.regExp.test(field.value + e.key)) {
        e.preventDefault();
        return true;
    }
    if (isString(field.value) && field.value.length) {
        if (includes(rule.runTime, RunTime.OnChange)) {
            field.value = (field.value as string).replace(rule.regExp, '');
            return true;
        }
        return !!rule.regExp.test(field.value);
    }
    return true;
};

export const getFieldName = (
    e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
    fieldName: string | undefined,
) => {
    return fieldName || e.currentTarget.name;
};

export const getViolatedRules = (field: Field, e?: React.KeyboardEvent<HTMLInputElement>): (Rule | CustomRule)[] => {
    const violatedRules: (Rule | CustomRule)[] = [];
    if (isArray(field.customRules)) {
        field.customRules.forEach(rule => {
            let result = true;
            result = testCustomRule(field, rule, e);
            if (!result) {
                violatedRules.push(rule);
            }
        });
    }
    if (isArray(field.rules)) {
        field.rules.forEach(rule => {
            let result = true;
            result = CheckValidationType[rule.type](field, e);
            if (!result) {
                violatedRules.push(rule);
            }
        });
    }
    return violatedRules;
};

function isCustomRule(rule: Rule | CustomRule): rule is CustomRule {
    if ((rule as CustomRule).regExp) {
        return true;
    }
    return false;
}

export const getViolatedRuleShouldBeDisplayed = (
    violatedRules: (Rule | CustomRule)[],
    ruleToHandle: FormValidationType[],
    runTime: RunTime,
): (Rule | CustomRule)[] => {
    return violatedRules.filter(rule => {
        if (isCustomRule(rule)) {
            return includes(rule.runTime, runTime);
        } else {
            return includes(ruleToHandle, rule.type);
        }
    });
};

export function processViolatedRules(
    field: Field,
    ruleToHandle: FormValidationType[],
    runTime: RunTime,
    e?: React.KeyboardEvent<HTMLInputElement>,
) {
    const violatedRules = getViolatedRules(field, e);

    return {
        ...field,
        isValid: violatedRules.length === 0,
        violatedRules: getViolatedRuleShouldBeDisplayed(violatedRules, ruleToHandle, runTime),
    };
}

export const useForm = (defaultForm: Form) => {
    const [form, setForm] = useState(defaultForm);
    const [isFormValid, setIsFormValid] = useState(false);
    useEffect(() => {
        let isFormValid = true;
        for (const key in form) {
            /* istanbul ignore next */
            if (Object.prototype.hasOwnProperty.call(form, key)) {
                const field = form[key];
                const violatedRules = getViolatedRules(field);
                if (violatedRules.length > 0) {
                    isFormValid = false;
                }
            }
        }
        setIsFormValid(isFormValid);
    }, [form]);
    const onBlur = (e: React.ChangeEvent<HTMLInputElement>, fieldName?: string) => {
        const localFieldName = getFieldName(e, fieldName);
        let field: Field = form[localFieldName];
        const ruleToHandle = [FormValidationType.Required];
        field = processViolatedRules(field, ruleToHandle, RunTime.OnBlur);

        setForm(pre => ({ ...pre, [localFieldName]: field }));
    };
    const onChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName?: string) => {
        const isCheckboxChange = e.currentTarget.type === 'checkbox';
        const localFieldName = getFieldName(e, fieldName);
        const ruleToHandle = [FormValidationType.NumberOnly, FormValidationType.MaxLength, FormValidationType.Required];
        let field = form[localFieldName];
        if (isCheckboxChange) {
            field.value = e.currentTarget.checked;
        } else {
            field.value = e.currentTarget.value;
        }

        field = processViolatedRules(field, ruleToHandle, RunTime.OnChange);
        setForm(pre => ({ ...pre, [localFieldName]: field }));
    };
    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, fieldName?: string) => {
        const localFieldName = getFieldName(e, fieldName);
        const ruleToHandle = [FormValidationType.NumberOnly, FormValidationType.MaxLength];
        let field = form[localFieldName];

        field = processViolatedRules(field, ruleToHandle, RunTime.OnKeyDown, e);
        setForm(pre => ({ ...pre, [localFieldName]: field }));
    };
    const getErrorMessage = (fieldName: string) => {
        const field: Field = form[fieldName];
        if (field.violatedRules[0]) {
            return field.violatedRules[0].errorMessage;
        } else {
            return '';
        }
    };

    const getValue = () => {
        const value: { [key: string]: string | number | boolean | null | undefined } = {};
        Object.keys(form).forEach(key => {
            value[key] = form[key].value;
        });
        return value;
    };

    const resetForm = () => {
        setForm(() => ({ ...defaultForm }));
    };

    return {
        form,
        onBlur,
        onChange,
        setForm,
        onKeyDown,
        getErrorMessage,
        isFormValid,
        getValue,
        resetForm,
    };
};
